import { Navigate, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { PageLoader } from '@/layouts/components/PageLoader/PageLoader';
import { Button } from '@/ui/components/Button';
import { Icon } from '@/ui/icons/Icon';
import { useKyc } from '@/features/kyc';
import { routes } from '@/routes/routes';
import { usePreliminaryOfferGeneration } from '../../hooks/usePreliminaryOfferGeneration';
import { OceanOfferCard } from '../../components/OceanOfferCard';
import styles from './PreliminaryOfferPage.module.scss';

// Tells the chat it opened this screen on facts the backend contradicted, so
// it asks before opening it again.
export interface PreliminaryOfferReturnState {
  isPreliminaryOfferOutOfSync: true;
}

/**
 * The preliminary offer priced on the BC score and declared income.
 *
 * Without KYC the page generates it and the user activates it (KYC, then ANAF
 * gives the final offer); no offer means the no-offer page pointing to the app
 * (low score), the refinance page (rejected on income or debt ratio), or the
 * no-score page (no BC score at all).
 * With KYC there is nothing to activate: the chat takes over and shows the
 * ANAF waiting screen.
 */
export function PreliminaryOfferPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isKycLoading, isKycValid } = useKyc(true);
  const { oceanOffers, hasOffers, hasGenerationFailed, isLoading, retry } =
    usePreliminaryOfferGeneration({
      // Without KYC: generate the offer and show it, or hand the user over.
      isEnabled: !isKycLoading && !isKycValid,
      onOutcome: (outcome) => {
        if (outcome === 'no-score') {
          navigate(routes.offer.preliminaryNoScore);
          return;
        }
        if (outcome === 'income-rejected') {
          navigate(routes.offer.preliminaryRefinance);
          return;
        }
        if (outcome === 'not-available') {
          navigate(routes.offer.preliminaryNotAvailable);
          return;
        }
        // The chat picks the step the backend still misses from fresh facts.
        void queryClient.invalidateQueries();
        const state: PreliminaryOfferReturnState = {
          isPreliminaryOfferOutOfSync: true,
        };
        navigate(routes.wizard.base, { replace: true, state });
      },
    });

  if (isKycLoading || isLoading) {
    return <PageLoader />;
  }

  // After KYC the final offer is on its way: the chat shows the ANAF waiting
  // screen.
  if (isKycValid) {
    return <Navigate to={routes.wizard.base} replace />;
  }

  if (!hasOffers && !hasGenerationFailed) {
    return <PageLoader />;
  }

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        <h1 className={styles.title}>Ofertele tale preliminare ✨</h1>

        {hasOffers ? (
          <div className={styles.offers}>
            {oceanOffers.map((offer) => (
              <OceanOfferCard
                key={offer.id}
                type={offer.type}
                amount={offer.amount}
                numberOfRates={offer.numberOfRates}
                trackingId={`preliminary_offer_details_${offer.type}`}
                onDetailsClick={() =>
                  navigate(routes.offer.preliminaryDetails(offer.type))
                }
              />
            ))}
          </div>
        ) : (
          <p className={styles.status}>
            Nu am putut calcula oferta din cauza unei probleme tehnice. Te
            rugăm să încerci din nou.
          </p>
        )}
      </div>

      {!hasOffers && (
        <div className={styles.bottomSection}>
          <Button
            trackingId="retry_preliminary_offer"
            text="Încearcă din nou"
            rightIcon={<Icon name="rotate-left" />}
            onClick={retry}
            className={styles.retryButton}
          />
        </div>
      )}
    </div>
  );
}

export default PreliminaryOfferPage;
